import { Component, Vue, Prop } from 'vue-property-decorator';
import { AxiosError, AxiosResponse } from 'axios';
import { ProductVariant } from '@/models/ProductVariant';
import ErrorHandler from '@/support/ErrorHandler';
import MiFileUpload from '@/components/mi-file-upload/MiFileUpload';
import { TableVisibility } from '@/components/data-table/DataTable';
import { fileToBase64 } from '@/support/String';
import { addClass, removeClass } from '@/support/Html';
import { objectToFormdata } from '@/support/Client';
import LoaderCard from '@/components/loader-card/LoaderCard';
import { Color } from '@/models/Color';
import { Size } from '@/models/Size';

@Component<UploadProductImageDialog>({
  components: {
    MiFileUpload,
    LoaderCard
  },
})
export default class UploadProductImageDialog extends Vue {
  @Prop({default: null})
  protected product!: ProductVariant;

  protected variant: ProductVariant | null = null;
  protected selectedVariants: ProductVariant[] = [];
  protected selectedVariantsFromTable: ProductVariant[] = [];
  protected selected: any[] = [];
  protected isLoading: boolean = false;
  protected activeStepper: boolean = true;
  protected currentStep: number = 1;

  protected visibility: TableVisibility = {
    checkboxes: true,
    limit: false,
    search: false,
    title: false,
    total: false,
    pagination: false,
    scrollable: true,
  };

  protected acceptedFileTypes = [
    'image/png',
    'image/jpeg',
    'image/svg+xml',
  ];

  protected uploadedFiles: UploadedFile[] = [];
  protected selectedFile: UploadedFile = {};
  protected defaultFileIndex: number | null = null;
  protected isViewingThumbnail: boolean = false;

  mounted() {
    const pageHtml = document.querySelectorAll('html')[0];
    addClass(pageHtml, 'overflow-y-hidden');
    this.variant = this.product;
    this.selectedVariants.push(this.variant);
  }

  protected goToStep(step: number) {
    if (this.currentStep < step || !this.activeStepper) {
      return;
    }

    this.currentStep = step;
  }

  protected combineSelectedVariants() {
    this.selectedVariants = [...this.selectedVariants, ...this.selected];
  }

  protected handleItemDropped(file: File) {
    if(!file) {
      return;
    }

    fileToBase64(file).then((encodedFile: string) => {
      const uploadedFile: UploadedFile = {
        url: encodedFile,
        name: file.name,
        file: file,
      };
      this.uploadedFiles.push(uploadedFile);
    });

    const fileUploadComponent = this.$refs.fileUpload as MiFileUpload;
    fileUploadComponent.uploaded();
  }

  get tableOptions() {
    return {
      name: {
        singular: 'Product',
        plural: 'Producten',
      },
      headers: [
        {
          text: 'Name',
          value: 'product_name',
        },
        {
          text: 'SKU',
          value: 'sku',
        },
        {
          text: 'Color',
          value: 'color',
          transform: (color: Color) => {
            return color ? color.name : '';
          }
        },
        {
          text: 'Size',
          value: 'size',
          transform: (size: Size) => {
            return size ? size.name : '';
          }
        },
      ],
      model: new ProductVariant().include(['color', 'size']).filter({product: this.product.product_uuid, exclude: this.product.id}),
    };
  }

  protected isViewingLargeThumbnail(uploadedFile: UploadedFile) {
    this.selectedFile = uploadedFile;
    this.isViewingThumbnail = true;
  }

  protected closeViewingThumbnailDialog() {
    this.isViewingThumbnail = false;
    this.selectedFile = {};
  }

  protected close() {
    this.$emit('input', false);
    this.currentStep = 1;
    this.clear();
    const pageHtml = document.querySelectorAll('html')[0];
    removeClass(pageHtml, 'overflow-y-hidden');
  }

  protected clear() {
    this.uploadedFiles = [];
    this.defaultFileIndex = null;
  }

  protected create() {
    let count = 0;
    this.isLoading = true;
    this.uploadedFiles.forEach((file: UploadedFile, index: number) => {
      let variants: {[key: string]: any} = [];

      this.selectedVariants.forEach((product: ProductVariant, index: number) => {
        variants.push(product.id);
      });

      let payload: {[key:string]: any} = {
        product_variants : variants,
        image: file.file,
      };

      if (this.defaultFileIndex !== null && this.defaultFileIndex === index) {
        payload = {...payload, ...{default: true}};
      }

      new ProductVariant()
      .attachImage()
      .create(objectToFormdata(payload))
      .then((response: AxiosResponse) => {
        this.currentStep = 5;
        count += 1;
        if (count === this.uploadedFiles.length) {
          this.$emit('imagesUploaded');
          this.isLoading = false;
          return;
        };
      })
      .catch((error: AxiosError) =>{
        ErrorHandler.network(error);
      })
    });
  }
}

interface UploadedFile {
  url?: string;
  name?: string;
  file?: any;
}
